import React, {useState} from "react";
import {GetVoices, InfoButton, Observer, RunInAction_Set, TextPlus, TimeToString} from "web-vcore";
import {Button, CheckBox, Column, Row, RowLR, Select, Spinner, Text, TextArea, TextInput} from "react-vcomponents";
import {BaseComponentPlus} from "react-vextensions";
import {store} from "../../../../Store/index.js";
import {InAndroid, nativeBridge} from "../../../../Utils/Bridge/Bridge_Native.js";
import {ShowMessageBox} from "react-vmessagebox";
import {ScrollView} from "react-vscrollview";
import {observer_mgl} from "mobx-graphlink";
import {LLMApi, MoreSettingsSubpanel, ParseProviderListFromProvidersStr} from "../../../../Store/main/settings.js";
import {GetEntries, StartUpload, emptyArray} from "js-vextensions";
import moment from "moment";
import {Filesystem, Directory, Encoding} from "@capacitor/filesystem";
import {GetSessions} from "../../../../Server/DB/sessions.js";
import {MeID} from "../../../../Server/DB/users.js";
import {GenerateBreathDataOutput, WriteBreathDataToFile} from "../../../Timeline/Sessions/SessionsUISettingsDropdown.js";
import {gdBelt_rawDataDB} from "../../../../Utils/IndexedDB/GDBelt_RawDataDB.js";
import {DataSubpanel} from "./More/Data.js";

export const SettingsUI_More = observer_mgl(()=>{
	const uiState = store.main.settings;
	return (
		<>
			<Row mt={5}>
				<Select displayType="button bar" options={GetEntries(MoreSettingsSubpanel, "ui")} value={uiState.more_subpanel} onChange={val=>uiState.more_subpanel = val}/>
			</Row>
			{uiState.more_subpanel == MoreSettingsSubpanel.web && <WebSubpanel/>}
			{uiState.more_subpanel == MoreSettingsSubpanel.info && <InfoSubpanel/>}
			{uiState.more_subpanel == MoreSettingsSubpanel.services && <ServicesSubpanel/>}
			{uiState.more_subpanel == MoreSettingsSubpanel.data && <DataSubpanel/>}
		</>
	);
});

const splitAt = 200;

const WebSubpanel = observer_mgl(()=>{
	const uiState = store.main.settings.web;
	return (
		<>
			<RowLR splitAt={splitAt - 10}>
				<TextPlus info={`
					This is appended to URLs when fetching some resources (eg. json files) from the web server.
					Example: Can be used for cache-busting. (eg. set to "?i=1", and/or increment that number, to force a file-refetch)
					Note: You may need to reopen the app/page, for this setting to take effect. (since some files are fetched at launch)
				`.AsMultiline(0)}>Fetch suffix (eg. json):</TextPlus>
				<TextInput value={uiState.fetchSuffix} onChange={val=>RunInAction_Set(()=>uiState.fetchSuffix = val)}/>
			</RowLR>
		</>
	);
});

export const ServicesSubpanel = observer_mgl(()=>{
	const uiState = store.main.settings;
	const addOpenRouterProvider = (providerName: string)=>{
		const newList = [
			...ParseProviderListFromProvidersStr(uiState.openRouterProviders),
			providerName,
		];
		RunInAction_Set(()=>uiState.openRouterProviders = newList.join(", "));
	};
	return (
		<>
			<Row mt={5}>
				<Text>LLM API:</Text>
				<Select ml={5} displayType="button bar" options={GetEntries(LLMApi, "ui")}
					value={uiState.llm_mainAPI} onChange={val=>RunInAction_Set(()=>uiState.llm_mainAPI = val)}/>
			</Row>

			<Row mt={10} style={{fontWeight: "bold"}}>{
				["OpenRouter", uiState.llm_mainAPI == LLMApi.openRouter && " (active)"].filter(a=>a).join(" ")
			}</Row>
			<Row center mt={5}>
				<Text>OpenRouter API key:</Text>
				<input type="password" style={{marginLeft: 5, flex: 1}} value={uiState.openRouterAPIKey ?? ""} onChange={e=>RunInAction_Set(()=>uiState.openRouterAPIKey = e.target.value)}/>
				<InfoButton ml={5} text={`
					Used for generating text for the "Story Explore" feature. (using the OpenRouter API)

					Note: This key is not stored in the database; it's only stored in your browser's local storage.
				`.AsMultiline(0)}/>
			</Row>
			<Row center mt={5}>
				<TextPlus sel info="See model list here: https://openrouter.ai/models?output_modalities=text">Model:</TextPlus>
				<TextInput ml={5} style={{width: 250}} value={uiState.openRouterModel} onChange={val=>RunInAction_Set(()=>uiState.openRouterModel = val)}/>
			</Row>
			<Row mt={5}>
				<Text mr={5}>Presets:</Text>
				<Button p="0 5px" text="GLM 5.3" onClick={()=>RunInAction_Set(()=>uiState.openRouterModel = "z-ai/glm-5.3")}/>
				<Button p="0 5px" text="GLM 5.2" onClick={()=>RunInAction_Set(()=>uiState.openRouterModel = "z-ai/glm-5.2")}/>
				<Button p="0 5px" text="GLM 5.1" onClick={()=>RunInAction_Set(()=>uiState.openRouterModel = "z-ai/glm-5.1")}/>
				<Button p="0 5px" text="GLM 4.7" onClick={()=>RunInAction_Set(()=>uiState.openRouterModel = "z-ai/glm-4.7")}/>
			</Row>
			<Row center mt={5}>
				<TextPlus sel info={`
					List of providers to use. (separated by commas -- whitespace at start/end of each entry is trimmed)
					* If empty: OpenRouter will use its default/auto-chosen provider.
					* If non-empty: OpenRouter will use the first provider in the list that is available.
					See provider list here: https://openrouter.ai/providers
				`.AsMultiline(0)}>Provider:</TextPlus>
				<TextInput ml={5} style={{width: 250}} value={uiState.openRouterProviders} onChange={val=>RunInAction_Set(()=>uiState.openRouterProviders = val)}/>
			</Row>
			<Row mt={5}>
				<Text mr={5}>Presets:</Text>
				<Button p="0 5px" text="Venice" onClick={()=>addOpenRouterProvider("venice/fp8")}/>
				<Button p="0 5px" text="Novita" onClick={()=>addOpenRouterProvider("novita/fp8")}/>
				<Button p="0 5px" text="Parasail" onClick={()=>addOpenRouterProvider("parasail/fp8")}/>
				<Button p="0 5px" text="Baseten" onClick={()=>addOpenRouterProvider("baseten/fp8")}/>
			</Row>
			<Row mt={5}>
				{/* downside: claimed fp8, but this suggests wrong: reddit.com/r/LocalLLaMA/comments/1nqkx7o */}
				<Button p="0 5px" text="Atlas Cloud" onClick={()=>addOpenRouterProvider("atlas-cloud/fp8")}/>
				{/* downside: leaves its claimed-quantization field blank */}
				<Button p="0 5px" text="Fireworks" onClick={()=>addOpenRouterProvider("fireworks")}/>
				{/* downside: limited models (but included for zdr claude-model support) */}
				<Button p="0 5px" text="Bedrock" onClick={()=>addOpenRouterProvider("amazon-bedrock")}/>
			</Row>
			<Row mt={5}>
				<TextPlus sel info={`
					Whether to send the {zdr: true, data_collection: "deny"} flags within the OpenRouter request.
					These flags block fulfillment of the request by providers that train on (data_collection) or even retain (zdr) prompt data.
					(this may lead to an error, if no providers matching your other requirements remain after that filtering)
				`.AsMultiline(0)}>Block training and retention:</TextPlus>
				<CheckBox ml={5} value={uiState.openRouter_blockTrainingAndRetention} onChange={val=>RunInAction_Set(()=>uiState.openRouter_blockTrainingAndRetention = val)}/>
			</Row>

			<Row mt={10} style={{fontWeight: "bold"}}>{
				["Anthropic", uiState.llm_mainAPI == LLMApi.anthropic && " (active)"].filter(a=>a).join(" ")
			}</Row>
			<Row center mt={5}>
				<Text>Anthropic API key:</Text>
				<input type="password" style={{marginLeft: 5, flex: 1}} value={uiState.anthropicAPIKey ?? ""} onChange={e=>RunInAction_Set(()=>uiState.anthropicAPIKey = e.target.value)}/>
				<InfoButton ml={5} text={`
					Used for generating text for the "Story Explore" feature. (using the Anthropic API)

					Note: This key is not stored in the database; it's only stored in your browser's local storage.
					Note: The Anthropic API is actually not used directly atm; OpenRouter is used instead. (later, may have toggle)
				`.AsMultiline(0)}/>
			</Row>
			<Row center mt={5}>
				<TextPlus sel info="See model list here: https://docs.anthropic.com/en/docs/about-claude/models">Model:</TextPlus>
				<TextInput ml={5} style={{width: 250}} value={uiState.anthropicModel} onChange={val=>RunInAction_Set(()=>uiState.anthropicModel = val)}/>
			</Row>
			<Row mt={5}>
				<Text mr={5}>Presets:</Text>
				<Button p="0 5px" text="4.8o" onClick={()=>RunInAction_Set(()=>uiState.anthropicModel = "claude-opus-4-8")}/>
				<Button p="0 5px" text="4.7o" onClick={()=>RunInAction_Set(()=>uiState.anthropicModel = "claude-opus-4-7")}/>
				<Button p="0 5px" text="4.6s" onClick={()=>RunInAction_Set(()=>uiState.anthropicModel = "claude-sonnet-4-6")}/>
				<Button p="0 5px" text="4.5o" onClick={()=>RunInAction_Set(()=>uiState.anthropicModel = "claude-opus-4-5-20251101")}/>
				<Button p="0 5px" text="4.5s" onClick={()=>RunInAction_Set(()=>uiState.anthropicModel = "claude-sonnet-4-5-20250929")}/>
				<Button p="0 5px" text="4.5h" onClick={()=>RunInAction_Set(()=>uiState.anthropicModel = "claude-haiku-4-5-20251001")}/>
				<Button p="0 5px" text="4s" onClick={()=>RunInAction_Set(()=>uiState.anthropicModel = "claude-sonnet-4-20250514")}/>
			</Row>

			<Row mt={10} style={{fontWeight: "bold"}}>Others</Row>
			<Row center mt={5}>
				<Text>ElevenLabs API key:</Text>
				<input type="password" style={{marginLeft: 5, flex: 1}} value={uiState.elevenLabsAPIKey ?? ""} onChange={e=>RunInAction_Set(()=>uiState.elevenLabsAPIKey = e.target.value)}/>
				<InfoButton ml={5} text={`
					Used for generating TTS audio for the "Visualization" feature using ElevenLabs voices.

					Note: This key is not stored in the database; it's only stored in your browser's local storage.
				`.AsMultiline(0)}/>
			</Row>
		</>
	);
});

export const InfoSubpanel = observer_mgl(()=>{
	const [memoryInfo, setMemoryInfo] = useState<object|n>(null);
	const uiState = store.main.settings;
	return (
		<>
			<Row mt={5}>
				<Button text="Refresh" onClick={async()=>{
					const memoryInfo = (await nativeBridge.Call("GetMemoryInfo")) as object;
					setMemoryInfo(memoryInfo);
				}}/>
				<Button ml={5} text="Clear MGL cache" onClick={async()=>{
					store.graphlink.ClearCaches();
				}}/>
				<Button ml={5} text="Clear->Resub MGL cache" onClick={async()=>{
					store.graphlink.SetUserInfo(store.graphlink.userInfo);
				}}/>
			</Row>
			{memoryInfo != null &&
			<Row>
				<TextArea autoSize={true} editable={false} value={JSON.stringify(memoryInfo, null, "\t")}/>
			</Row>}
		</>
	);
});